import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./MemoryGame.css";
import "./ChessGame.css";
import ChessChat from "./ChessChat";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import { memoryLeave, memoryPresence, memorySelect } from "../services/memoryApi";

let pendingLeave = null;

const fill = (template, name) => template.replace("{name}", name);

const shuffle = (list) => {
  const next = [...list];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [next[index], next[swap]] = [next[swap], next[index]];
  }
  return next;
};

const loadDeck = async () => {
  const response = await fetch(`${process.env.PUBLIC_URL}/memory/cards.json`);
  if (!response.ok) throw new Error("cards");
  const data = await response.json();
  return shuffle(
    (data.cards || []).map((card) => ({
      id: card.id,
      pairId: card.pairId,
      label: card.label,
      image: `${process.env.PUBLIC_URL}/memory/${card.image}`,
    }))
  );
};

const groupOpponents = (opponents) => {
  const groups = new Map();
  opponents.forEach((player) => {
    const first = (player.displayName || "").trim().charAt(0).toUpperCase();
    const key = /[A-Z]/.test(first) ? first : "#";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(player);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([letter, players]) => [
      letter,
      [...players].sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" })
      ),
    ]);
};

function MemoryMenu({ id, label, placeholder, valueLabel, children }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`memory-menu ${open ? "is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="memory-menu-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={valueLabel ? "" : "is-placeholder"}>{valueLabel || placeholder}</span>
        <span className="memory-menu-caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="memory-menu-panel" role="listbox" aria-label={label || undefined}>
          {typeof children === "function" ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}

const MemoryGame = () => {
  const { t } = useLanguage();
  const { userId, displayName } = useFirebase();
  const [screen, setScreen] = useState("setup");
  const [playMode, setPlayMode] = useState("");
  const [opponents, setOpponents] = useState([]);
  const [opponentId, setOpponentId] = useState("");
  const [onlineGame, setOnlineGame] = useState(null);
  const [deck, setDeck] = useState([]);
  const [faceUp, setFaceUp] = useState([]);
  const [matched, setMatched] = useState(() => new Set());
  const [collected, setCollected] = useState([]);
  const [preview, setPreview] = useState(false);
  const [links, setLinks] = useState([]);
  const [linkFrame, setLinkFrame] = useState({ width: 0, height: 0 });
  const tileMarkRefs = useRef(new Map());
  const bubbleMarkRefs = useRef([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const ignoredGameRef = useRef(null);
  const userIdRef = useRef(userId);
  const playModeRef = useRef(playMode);
  const screenRef = useRef(screen);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    playModeRef.current = playMode;
  }, [playMode]);

  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  useEffect(() => {
    if (pendingLeave) {
      clearTimeout(pendingLeave);
      pendingLeave = null;
    }
    return () => {
      const id = userIdRef.current;
      pendingLeave = setTimeout(() => {
        pendingLeave = null;
        memoryLeave(id);
      }, 700);
    };
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const data = await memoryPresence(
          userId,
          displayName,
          screenRef.current === "setup"
        );
        if (cancelled) return;
        setOpponents(data.opponents || []);
        setError((prev) => (prev ? "" : prev));
        if (
          data.game &&
          data.game.id !== ignoredGameRef.current &&
          playModeRef.current === "human"
        ) {
          setOnlineGame(data.game);
          setScreen("board");
        }
      } catch {
        if (!cancelled) setError(tRef.current("memoryConnectError"));
      }
    };

    tick();
    const interval = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId, displayName]);

  useLayoutEffect(() => {
    if (!preview || faceUp.length !== 2) {
      setLinks([]);
      return undefined;
    }
    const measure = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const next = faceUp
        .map((id, index) => {
          const fromEl = bubbleMarkRefs.current[index];
          const toEl = tileMarkRefs.current.get(id);
          if (!fromEl || !toEl) return null;
          const from = fromEl.getBoundingClientRect();
          const to = toEl.getBoundingClientRect();
          return {
            id,
            x1: from.left + from.width / 2,
            y1: from.top + from.height / 2,
            x2: to.left + to.width / 2,
            y2: to.top + to.height / 2,
          };
        })
        .filter(Boolean);
      setLinkFrame({ width, height });
      setLinks(next);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [preview, faceUp]);

  useEffect(() => {
    if (!opponentId) return;
    if (!opponents.some((player) => player.userId === opponentId)) setOpponentId("");
  }, [opponents, opponentId]);

  const groups = groupOpponents(opponents);
  const chosenPerson = opponents.find((player) => player.userId === opponentId);
  const isHuman = playMode === "human";
  const isPractice = playMode === "practice";
  const canGo = !sending && (isPractice || (isHuman && Boolean(opponentId)));
  const opponentName = onlineGame?.opponent?.displayName || t("memoryOpponent");
  const opponentLeft = onlineGame?.status === "left";
  const allOpen = deck.length > 0 && matched.size === deck.length;
  const wantLabel = isPractice ? t("memoryPractice") : isHuman ? t("memoryHuman") : "";

  const chooseMode = async (nextMode) => {
    if (nextMode === playMode) return;
    if (nextMode !== "human") {
      setOnlineGame(null);
      setOpponentId("");
    }
    setPlayMode(nextMode);
    setError("");
  };

  const startPractice = async () => {
    const nextDeck = await loadDeck();
    setDeck(nextDeck);
    setFaceUp([]);
    setMatched(new Set());
    setCollected([]);
    setPreview(false);
    setOnlineGame(null);
    setScreen("board");
  };

  const handleGo = async () => {
    if (!canGo) return;
    setSending(true);
    setError("");
    try {
      if (isPractice) {
        await startPractice();
        return;
      }
      const data = await memorySelect(userId, displayName, opponentId);
      if (data.game) {
        ignoredGameRef.current = null;
        setOnlineGame(data.game);
        setScreen("board");
      }
    } catch (err) {
      if (err.payload?.opponents) setOpponents(err.payload.opponents);
      setError(err.payload?.error || (isPractice ? t("memoryCardsError") : t("memoryAlreadyPlaying")));
    } finally {
      setSending(false);
    }
  };

  const previewCards =
    preview && faceUp.length === 2
      ? faceUp.map((id) => deck.find((entry) => entry.id === id)).filter(Boolean)
      : [];

  const turnCard = (card) => {
    if (!isPractice || preview || allOpen) return;
    if (matched.has(card.id) || faceUp.includes(card.id) || faceUp.length >= 2) return;
    const next = [...faceUp, card.id];
    setFaceUp(next);
    if (next.length === 2) setPreview(true);
  };

  const closePreview = () => {
    const [first, second] = previewCards;
    if (first && second && first.pairId === second.pairId) {
      setMatched((current) => {
        const updated = new Set(current);
        updated.add(first.id);
        updated.add(second.id);
        return updated;
      });
      setCollected((current) => [...current, first, second]);
    }
    setFaceUp([]);
    setPreview(false);
  };

  return (
    <div className="memory-container">
      <div className={`memory-card ${screen === "board" ? "is-playing" : ""} ${isPractice && screen === "board" ? "is-board" : ""} ${isHuman && screen === "board" ? "is-match" : ""}`}>
        {screen === "setup" ? (
          <>
            <h1 className="memory-title">{displayName || t("memoryYou")}</h1>
            <div className="memory-lobby">
              <label className="memory-label" htmlFor="memory-want">
                {t("memoryIfYouWant")}
              </label>
              <MemoryMenu
                id="memory-want"
                label={t("memoryIfYouWant")}
                placeholder={t("memoryWantPlaceholder")}
                valueLabel={wantLabel}
              >
                {(close) =>
                  [
                    ["practice", t("memoryPractice")],
                    ["human", t("memoryHuman")],
                  ].map(([mode, name]) => (
                    <button
                      key={mode}
                      type="button"
                      role="option"
                      aria-selected={playMode === mode}
                      className={`memory-menu-item ${playMode === mode ? "is-selected" : ""}`}
                      onClick={() => {
                        chooseMode(mode);
                        close();
                      }}
                    >
                      {name}
                    </button>
                  ))
                }
              </MemoryMenu>
            </div>

            {isHuman && (
              <div className="memory-lobby">
                <label className="memory-label" htmlFor="memory-player">
                  {t("memoryPlayerLabel")}
                </label>
                <MemoryMenu
                  id="memory-player"
                  label={t("memoryPlayerLabel")}
                  placeholder={t("memoryPlayerPlaceholder")}
                  valueLabel={chosenPerson?.displayName || ""}
                >
                  {(close) => {
                    if (groups.length === 0) {
                      return <p className="memory-menu-empty">{t("memoryNoOpponents")}</p>;
                    }
                    return groups.map(([letter, players]) => (
                      <div key={letter} className="memory-menu-group">
                        <div className="memory-menu-letter">{letter}</div>
                        {players.map((player) => (
                          <button
                            key={player.userId}
                            type="button"
                            role="option"
                            aria-selected={opponentId === player.userId}
                            disabled={player.busy}
                            className={`memory-menu-item ${
                              opponentId === player.userId ? "is-selected" : ""
                            } ${player.busy ? "is-busy" : ""}`}
                            onClick={() => {
                              if (player.busy) return;
                              setOpponentId(player.userId);
                              close();
                            }}
                          >
                            {player.displayName}
                          </button>
                        ))}
                      </div>
                    ));
                  }}
                </MemoryMenu>
                {opponents.length > 0 && <p className="memory-note">{t("memoryYouAreListed")}</p>}
              </div>
            )}

            <div className="memory-actions">
              <button
                type="button"
                className="memory-primary"
                disabled={!canGo}
                onClick={handleGo}
              >
                {sending ? t("memoryGoing") : t("memoryGo")}
              </button>
            </div>
            {error && <p className="memory-error">{error}</p>}
            {isHuman && opponents.length === 0 && (
              <p className="memory-note">{t("memoryNoOpponents")}</p>
            )}
          </>
        ) : isPractice ? (
          <div className={`memory-play ${preview ? "is-preview" : ""}`}>
            <h1 className="memory-title">{displayName || t("memoryYou")}</h1>
            <p className="memory-status">{allOpen ? t("memoryAllOpen") : t("memoryYourTurn")}</p>
            {error && <p className="memory-error">{error}</p>}
            <div className="memory-board">
              {deck.map((card) => {
                if (matched.has(card.id)) return null;
                const open = faceUp.includes(card.id);
                return (
                  <button
                    key={card.id}
                    type="button"
                    className="memory-tile"
                    aria-label={card.label}
                    disabled={open || preview || allOpen}
                    onClick={() => turnCard(card)}
                  >
                    {open ? (
                      <img src={card.image} alt={card.label} />
                    ) : (
                      <span className="memory-tile-back" />
                    )}
                    {open && (
                      <span
                        className={`memory-tile-mark ${faceUp[1] === card.id ? "is-second" : ""}`}
                        ref={(node) => {
                          if (node) tileMarkRefs.current.set(card.id, node);
                          else tileMarkRefs.current.delete(card.id);
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="memory-pocket">
              <button type="button" className="memory-pocket-count" aria-label={t("memoryYouHave").replace("{count}", String(collected.length))}>
                {collected.length}
              </button>
              {collected.length > 0 && (
                <div className="memory-pocket-row">
                  {collected.map((card) => (
                    <img key={card.id} src={card.image} alt={card.label} />
                  ))}
                </div>
              )}
            </div>
            {links.length > 0 &&
              createPortal(
                <svg
                  className="memory-links"
                  width={linkFrame.width}
                  height={linkFrame.height}
                  viewBox={`0 0 ${linkFrame.width} ${linkFrame.height}`}
                  aria-hidden="true"
                >
                  {links.map((link) => (
                    <g key={link.id}>
                      <line className="memory-link-outline" x1={link.x1} y1={link.y1} x2={link.x2} y2={link.y2} />
                      <line className="memory-link" x1={link.x1} y1={link.y1} x2={link.x2} y2={link.y2} />
                    </g>
                  ))}
                </svg>,
                document.body
              )}
            {previewCards.length === 2 && (
              <div className="memory-previews">
                {previewCards.map((card, index) => (
                  <div key={card.id} className="memory-preview">
                    <div className="memory-preview-frame">
                      <img src={card.image} alt={card.label} />
                      {index === 1 && (
                        <button
                          type="button"
                          className="memory-preview-close"
                          aria-label={t("memoryClose")}
                          onClick={closePreview}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <span className="memory-preview-tail" aria-hidden="true" />
                    <span
                      className={`memory-preview-mark ${index === 1 ? "is-second" : ""}`}
                      ref={(node) => {
                        bubbleMarkRefs.current[index] = node;
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="memory-match">
            <div className="memory-play">
              <div className="memory-players">
                <div>{opponentName}</div>
                <div>{displayName || t("memoryYou")}</div>
              </div>
              <p className="memory-status">
                {opponentLeft
                  ? onlineGame?.leftBy === userId
                    ? t("memoryYouLeft")
                    : t("memoryOpponentLeft")
                  : fill(t("memoryPlayingWith"), opponentName)}
              </p>
              {error && <p className="memory-error">{error}</p>}
            </div>
            {onlineGame?.id && (
              <ChessChat
                gameId={onlineGame.id}
                userId={userId}
                displayName={displayName}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryGame;
